"use client";

import React, { useState } from "react";
import { Maximize2, X, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription, DialogHeader } from "@/components/ui/dialog.tsx";
import { AspectRatio } from "@/components/ui/aspect-ratio.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const galleryItems = [{
  id: 1,
  src: "https://coastal-prestige.websiteshop.au/wp-content/uploads/2026/03/20250917_101339-scaled.jpg?v=ftm4slubz5a",
  alt: "Our SCAG Lawn Mower keeps our work efficient and consistent.",
  category: "Our Machine",
  className: "col-span-1 md:col-span-2 md:row-span-2",
  ratio: 1 / 1
}, {
  id: 2,
  src: "https://coastal-prestige.websiteshop.au/wp-content/uploads/2026/03/20230804_145536-1-scaled.jpg?v=1z7ueg0l8wq",
  alt: "Looking after your gardens and hedges with care",
  category: "Garden care",
  className: "col-span-1 md:col-span-1 md:row-span-1",
  ratio: 1 / 1
}, {
  id: 3,
  src: "https://coastal-prestige.websiteshop.au/wp-content/uploads/2026/03/20260119_085501-scaled.jpg?v=1hnch6wynbx",
  alt: "Careful Backyard Maintenance",
  category: "Backyard Excellence",
  className: "col-span-1 md:col-span-1 md:row-span-2",
  ratio: 9 / 16
}, {
  id: 4,
  src: "https://coastal-prestige.websiteshop.au/wp-content/uploads/2026/03/20260124_110632-2-scaled.jpg?v=mhb16ytx8ua",
  alt: "Professionally Maintained Commercial Spaces",
  category: "Commercial Spaces",
  className: "col-span-1 md:col-span-1 md:row-span-1",
  ratio: 1 / 1
}, {
  id: 5,
  src: "https://coastal-prestige.websiteshop.au/wp-content/uploads/2026/03/pressure_washing.webp?v=e5z60k211h",
  alt: "Pressure Washing using the best tools to keep your surfaces looking perfect",
  category: "Pressure Washing",
  className: "col-span-1 md:col-span-2 md:row-span-1",
  ratio: 16 / 9
}, {
  id: 6,
  src: "https://coastalprestigemowing.com.au/wp-content/uploads/2026/04/TK1_1473.jpg?v=ph5sjtln75m",
  alt: "Our Ride on allows us to get a consistent and efficient cut every time.",
  category: "Ride On",
  className: "col-span-1 md:col-span-1 md:row-span-1",
  ratio: 1 / 1
}, {
  id: 7,
  src: "https://coastal-prestige.websiteshop.au/wp-content/uploads/2026/03/b5089389-42e7-40ad-8ee9-5531464e070d.jpeg?v=81vv28lyujx",
  alt: "A close up of our trusty base of operations.",
  category: "Our Rig",
  className: "col-span-1 md:col-span-1 md:row-span-1",
  ratio: 1 / 1
}];
export default function MasteryShowcase() {
  const [selectedImage, setSelectedImage] = useState<(typeof galleryItems)[0] | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="mastery-showcase" className="dark bg-background text-foreground py-20 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 text-center space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">
            Our Mastery in Motion
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Explore our portfolio of precision lawn care and property maintenance across the Central Coast.
            From razor-sharp edges to perfectly manicured hedges.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[minmax(200px,auto)] transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {galleryItems.map((item, i) => <div key={item.id} data-index={i} className={cn("group relative overflow-hidden rounded-xl bg-card border border-border/50 shadow-sm cursor-pointer", item.className)} onClick={() => setSelectedImage(item)}>
              {i === 2 || i === 5 || i === 6 ? <div className="h-full w-full">
                  <img src={item.src} alt={item.alt} className={cn("h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105", i === 5 && "object-[35%_center]")} />
                  <div className="absolute inset-0 bg-black/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col items-center justify-center text-white p-4">
                    <Maximize2 className="w-8 h-8 mb-2 text-primary opacity-0 -translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 delay-100" />
                    <span className="text-sm font-medium tracking-wider uppercase opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 delay-150">
                      View Detail
                    </span>
                    <span className="mt-2 text-xs text-white opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 delay-200">
                      {item.category}
                    </span>
                  </div>
                </div> : <AspectRatio ratio={item.ratio} className="h-full w-full">
                  <img src={item.src} alt={item.alt} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col items-center justify-center text-white p-4">
                    <Maximize2 className="w-8 h-8 mb-2 text-primary opacity-0 -translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 delay-100" />
                    <span className="text-sm font-medium tracking-wider uppercase opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 delay-150">
                      View Detail
                    </span>
                    <span className="mt-2 text-xs text-white opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 delay-200">
                      {item.category}
                    </span>
                  </div>
                </AspectRatio>}
            </div>)}
        </div>

        <div className="mt-12 text-center">
          <Link to="#">
            <Button size="lg" className="group">
              View All Services
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>

      {}
      <Dialog open={!!selectedImage} onOpenChange={open => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-5xl w-full bg-background/95 backdrop-blur-xl border-border p-1 sm:p-2 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>{selectedImage?.category || "Gallery Image"}</DialogTitle>
            <DialogDescription>{selectedImage?.alt || "Detail view of our work"}</DialogDescription>
          </DialogHeader>
          
          <div className="relative w-full h-[80vh] rounded-lg overflow-hidden bg-black flex items-center justify-center">
             {selectedImage && <img src={selectedImage.src} alt={selectedImage.alt} className="max-w-full max-h-full object-contain" />}
             <DialogClose className="absolute top-4 right-4 z-50 rounded-full bg-black/50 p-2 text-white hover:bg-primary hover:text-primary-foreground transition-colors">
                <X className="h-6 w-6" />
                <span className="sr-only">Close</span>
             </DialogClose>
          </div>
          
          {selectedImage && <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent text-white">
                <h3 className="text-xl font-bold text-white">{selectedImage.category}</h3>
                <p className="text-sm text-gray-200 mt-1">{selectedImage.alt}</p>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}